import { getRandomID, getValidJSON, getPercent } from "./common";
import {
  YESNO,
  TEST_TYPE,
  TASK_TYPES,
  DEVICE_TYPE,
  URL_PATTERN,
  ANSWER_TYPE,
  LIKERT_TYPES,
  SURVEY_TYPES,
  TASK_TYPE_KEYS,
  SUCCESS_CRITERIA,
  SIMPLE_INPUT_TYPES,
  MATRIX_INPUT_TYPES,
  SHUFFLE_OPTIONS_TYPES,
  TASK_STATUS,
  CARD_SORT_TYPES,
  REQUIRE_EXPLANATION,
} from "../actions/constants";
import { getDefaultTree } from "../components/toolkit/utills/treeFunctions";
import { getNodePath } from "./report_tree_functions";
import { flattenTasks } from "./logicUtils";

const { EMPTY, BLANK, IMAGE, SURVEY, DEFAULT, EXTERNAL_PROTOTYPE, FIVESEC, FIRSTCLICK, TREE, CARDSORT, HTML_PROTOTYPE } = TASK_TYPES;
const { RADIO, CHECKBOX, TEXT, LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, NOTE, RANKING, SIMPLE_INPUT, MATRIX, NPS } = SURVEY_TYPES;
const {
  NOTE_TASK,
  YESNO_QUESTION,
  RATING_QUESTION,
  OPEN_QUESTION,
  RANK_OPTIONS,
  MULTIPLE_TYPE,
  WEBSITE_TYPE,
  PROTOTYPE_TASK,
  FIVESECOND_TASK,
  IMAGE_TASK,
  SPEAKING_TASK,
  IOS_APP_TASK,
  ANDROID_APP_TASK,
  MULTIOS_APP_TASK,
  FIVESEC_TASK,
  FIRSTCLICK_TASK,
  SIMPLE_INPUT_QUESTION,
  MATRIX_QUESTION_TASK,
  TREE_TASK,
  CARDSORT_TASK,
  NPS_QUESTION,
  HTML_PROTOTYPE_TASK,
} = TASK_TYPE_KEYS;

export const getTaskTypes = (test_type = null, pcheck = YESNO.NO, fetchAll = false) => {
  const types = [
    {
      key: NOTE_TASK,
      title: "Note",
      icon: "task_note",
      category: "Instructions",
      params: { type: SURVEY, surveyType: NOTE, five_second: 0 },
      description: "Display context or instructions to participants before a task",
    },
    {
      key: PROTOTYPE_TASK,
      title: "Figma Prototype",
      icon: "task_prototype",
      category: "Usability",
      params: { type: EXTERNAL_PROTOTYPE, surveyType: null, five_second: 0 },
      description: "Test user flows on an interactive prototype",
    },
    {
      key: HTML_PROTOTYPE_TASK,
      title: "HTML Prototype",
      icon: "task_html_prototype",
      category: "Usability",
      params: { type: HTML_PROTOTYPE, surveyType: null, five_second: 0 },
      description: "Test an HTML prototype on a live link, such as one built with Claude or Lovable",
    },
    {
      key: WEBSITE_TYPE,
      title: "Website",
      icon: "task_website",
      category: "Usability",
      params: { type: DEFAULT, surveyType: null, five_second: 0 },
      description: "Have participants interact with a live, published website",
    },
    {
      key: MULTIOS_APP_TASK,
      title: "iOS/Android App",
      icon: "task_multios",
      category: "Usability",
      params: { type: DEFAULT, surveyType: null, five_second: 0 },
      description: "Test tasks on a live app across both iOS and Android",
    },
    {
      key: IOS_APP_TASK,
      title: "iOS App",
      icon: "task_ios",
      category: "Usability",
      params: { type: DEFAULT, surveyType: null, five_second: 0 },
      description: "Test tasks on a live app from the App Store",
    },
    {
      key: ANDROID_APP_TASK,
      title: "Android App",
      icon: "task_android",
      category: "Usability",
      params: { type: DEFAULT, surveyType: null, five_second: 0 },
      description: "Test tasks on a live app from the Google Play Store",
    },
    {
      key: FIRSTCLICK_TASK,
      title: "First-Click",
      icon: "task_firstclick",
      category: "Usability",
      params: { type: FIRSTCLICK, surveyType: null, five_second: 0 },
      description: "See where participants click first to complete a task",
    },
    {
      key: CARDSORT_TASK,
      title: "Card Sort",
      icon: "task_cardsort",
      category: "Usability",
      params: { type: CARDSORT, surveyType: null, five_second: 0 },
      description: "Discover how participants group and label information",
    },
    {
      key: TREE_TASK,
      title: "Tree Test",
      icon: "task_tree",
      category: "Usability",
      params: { type: TREE, surveyType: null, five_second: 0 },
      description: "Test how easily participants find items in a website or app navigation",
    },
    {
      key: FIVESEC_TASK,
      title: "5-Second",
      icon: "task_5sec",
      category: "Usability",
      params: { type: FIVESEC, surveyType: null, five_second: 1 },
      description: "Show participants an image for 5-10 seconds to test first impressions",
    },
    {
      key: FIVESECOND_TASK,
      title: "Time Limited",
      icon: "task_5second",
      category: "Usability",
      params: { type: pcheck === YESNO.NO ? DEFAULT : EXTERNAL_PROTOTYPE, surveyType: null, five_second: 1 },
      description: "Display content for a set duration to measure recall",
    },
    {
      key: YESNO_QUESTION,
      title: "Yes/No",
      icon: "task_yesno",
      category: "Survey",
      params: { type: SURVEY, surveyType: RADIO, five_second: 0 },
      description: "Ask a simple question with yes or no choices",
    },
    {
      key: OPEN_QUESTION,
      title: "Open Question",
      icon: "task_openquestion",
      category: "Survey",
      params: { type: SURVEY, surveyType: TEXT, five_second: 0 },
      description: "Collect open-ended feedback from participants, with optional AI follow-ups",
    },
    {
      key: MULTIPLE_TYPE,
      title: "Multiple Choice",
      icon: "task_checkbox",
      category: "Survey",
      params: { type: SURVEY, surveyType: CHECKBOX, five_second: 0 },
      description: "Offer a list of options for single or multiple selection",
    },
    {
      key: RANK_OPTIONS,
      title: "Rank Options",
      icon: "task_ranking",
      category: "Survey",
      params: { type: SURVEY, surveyType: RANKING, five_second: 0 },
      description: "Ask participants to order items by preference or priority",
    },
    {
      key: RATING_QUESTION,
      title: "Rating Scale",
      icon: "task_rating",
      category: "Survey",
      params: { type: SURVEY, surveyType: [LIKERT_5PT, LIKERT_7PT, LIKERT_10PT], five_second: 0 },
      description: "Ask participants to rate an item using a customizable scale",
    },
    {
      key: NPS_QUESTION,
      title: "Net Promoter Score",
      icon: "task_nps",
      category: "Survey",
      params: { type: SURVEY, surveyType: NPS, five_second: 0 },
      description: "Measure loyalty with a 0-10 likelihood-to-recommend Net Promoter Score (NPS)",
    },
    {
      key: MATRIX_QUESTION_TASK,
      title: "Matrix Question",
      icon: "task_matrix",
      category: "Survey",
      params: { type: SURVEY, surveyType: MATRIX, five_second: 0 },
      description: "Have participants rate multiple items using the same rating scale",
    },
    {
      key: SIMPLE_INPUT_QUESTION,
      title: "Simple Input",
      icon: "task_simpleinput",
      category: "Survey",
      params: { type: SURVEY, surveyType: SIMPLE_INPUT, five_second: 0 },
      description: "Collect short answers like text, numbers, dates, or emails",
    },
    {
      key: IMAGE_TASK,
      title: "Image",
      icon: "task_image",
      category: "Survey",
      params: { type: IMAGE, surveyType: null, five_second: 0 },
      description: "Display an image or design mockup to gather feedback",
    },
    {
      key: SPEAKING_TASK,
      title: "Speaking",
      icon: "task_speaking",
      category: "Survey",
      params: { type: BLANK, surveyType: null, five_second: 0 },
      description: "Ask participants to record their thoughts out loud",
    },
  ];

  // Unsupported task keys per test type — mark disabled instead of removing
  let unsupportedKeys = [];
  if (Number(test_type) === TEST_TYPE.WEBSITE) unsupportedKeys = [IOS_APP_TASK, ANDROID_APP_TASK, MULTIOS_APP_TASK];
  else if (Number(test_type) === TEST_TYPE.ANDROID_APP) unsupportedKeys = [IOS_APP_TASK, MULTIOS_APP_TASK, WEBSITE_TYPE, HTML_PROTOTYPE_TASK];
  else if (Number(test_type) === TEST_TYPE.IOS_APP) unsupportedKeys = [WEBSITE_TYPE, ANDROID_APP_TASK, MULTIOS_APP_TASK, HTML_PROTOTYPE_TASK];
  else if (Number(test_type) === TEST_TYPE.MULTIOS_APP) unsupportedKeys = [WEBSITE_TYPE, ANDROID_APP_TASK, IOS_APP_TASK, HTML_PROTOTYPE_TASK];

  // Fetch all task types
  if (fetchAll) return types.map((t) => ({ ...t, disabled: unsupportedKeys.includes(t.key) }));

  // Filter out unsupported task types
  return types.filter((t) => !unsupportedKeys.includes(t.key));
};

export const getTaskType = (taskType = null, pcheck = YESNO.NO, isSurveyTest = false) => {
  // Empty Task
  if (taskType === EMPTY) {
    return {
      key: EMPTY,
      icon: "task_empty",
      title: `Select a ${isSurveyTest ? "question" : "task"}`,
      params: { type: EMPTY, surveyType: null, five_second: 0 },
    };
  }
  const taskTypes = getTaskTypes(null, pcheck);
  return taskTypes.find(({ key }) => key === taskType);
};

export const getEmptySuccessURL = (url = "", urlpattern = URL_PATTERN.EXACT) => {
  return {
    urlpattern,
    url: Number(urlpattern) === URL_PATTERN.PATH_URLS ? JSON.stringify([url]) : url,
  };
};

// Migrate Array of Strings to Array of Objects
export const getSurveyOptionObj = (question_id = "", name = "", position = 0, source_name = "", image = "", id = "", is_fixed) => {
  const surveyId = id || getRandomID();
  return { id: surveyId, name, question_id, position, image, source_name, is_fixed };
};
export const getSurveyOptionsObj = (question_id = "", options = []) => {
  return options.map((name, position) => {
    if (typeof name === "object") return { ...name, position };
    return getSurveyOptionObj(question_id, name, position);
  });
};

// Create Options from Images
export const getSurveyOptionsFromImages = (images = [], activeTask = {}) => {
  const { id = getRandomID(), options = [] } = activeTask || {};

  return images?.map(({ fileName = "", imageName = "" }, position) => {
    const optionId = options?.[position]?.id || getRandomID();
    // map is_fixed from normal option to image options
    const is_fixed = Number(options?.[position]?.is_fixed) || 0;
    return getSurveyOptionObj(id, imageName, position, "", fileName, optionId, is_fixed);
  });
};

export const getTaskObj = (position = 0, otherAttrs, uiTaskType = null) => {
  const id = getRandomID(); // Random Unique ID
  const { surveyType } = otherAttrs;
  const likertOptionType = LIKERT_TYPES.CUSTOM;
  const options = [LIKERT_5PT, LIKERT_7PT, LIKERT_10PT].includes(surveyType)
    ? getLikertScaleOptions(id, likertOptionType, surveyType)
    : surveyType === NPS
      ? Array.from({ length: 11 }, (_, i) => String(i)) // always fixed 0-10, never editable
      : uiTaskType === YESNO_QUESTION
        ? getYesNoOptions(id)
        : [getSurveyOptionObj(id)];

  const isMatrixQuestionTask = uiTaskType === MATRIX_QUESTION_TASK;
  const criterias = [getSurveyOptionObj(id), getSurveyOptionObj(id)];

  return {
    id,
    url: "",
    isNew: true,
    start_urls: { ios: "", android: "" },
    position,
    answer: "",
    title: "",
    type: null,
    subTasks: [], // used incase of GroupType Task
    five_second: 0,
    task_shuffle: 0,
    description: otherAttrs?.surveyType === NPS ? "How likely are you to recommend us on a scale from 0 to 10." : "",
    addImages: false,
    use_task_type: 0, // no longer in used
    expected_time: 0,
    prototype_id: null,
    html_prototype_id: null, // HTML prototype task: uxarmy_html_prototypes.id (Nest)
    proto_rules: null, // HTML prototype task: success rules, see getEmptyProtoRules()
    proto_settings: null, // HTML prototype task: { replay, mouseTrail, mode }, set in getUiTaskObj
    required: YESNO.YES,
    show_alert: YESNO.NO, // used in case of 5SecTask & FirstClickTask - Show reminder alert
    delay_success_msg: 1, // used in case of FigmaTask, delay in showing the success message.
    answer_type: ANSWER_TYPE.TEXT,
    success_type: SUCCESS_CRITERIA.NONE,
    task_image_url: "", // used in case of ImageTask, pull full path of S3.
    use_post_ques: 0, // true-false if we're using the survey question.
    surveyQuestions: [], // put survey questions
    input_type: SIMPLE_INPUT_TYPES.TEXT,
    mobile_app_name: "", // used in case of Android/ios app navigation task
    use_prevtask_url: 0, // used when we want to use the previous task's end url as the start url for the next task.
    successUrlsArr: [],
    // survey questions attributes - merged into the task object on 3rd Apr 2023, discussed with Harsh, Charu, Abhinav & Tikam.
    surveyType: null, // 'type' in the surveyQuestion
    images: [],
    addMaybe: false,
    useOtherOption: 0,
    shuffle_images: 0,
    likertOptionType,
    allowMultiSelect: false,
    options: isMatrixQuestionTask ? criterias : getSurveyOptionsObj(id, options),
    use_options_as_img: false,
    oldOptions: [],
    translation: { tool: null, lang: null },
    source_description: "",
    ai_follow_up_config: { context: "", enabled: false, questions: 1 },
    shuffle_options: SHUFFLE_OPTIONS_TYPES.NONE,
    shuffle_criterias: SHUFFLE_OPTIONS_TYPES.NONE, // used in case of Matrix Question Task, to randomize the rows
    criterias, // used in case of Matrix Question Task, to store the criteria for each row
    prev_answers_config: { enabled: false, task_ids: [] },
    tree: { id: getRandomID(), treeData: [], ...getDefaultTree() }, // Tree Task
    tree_answer: [], // Tree Task Answers
    cards: [],
    categories: [],
    card_settings: JSON.stringify({
      card: {
        shuffle: true,
        max_cards: 250, // max cards can be created
        description: true,
        sort_all_card: true,
        order_indicator: false,
        cards_to_show: null, // max number of cards to show to testers.
      },
      category: {
        shuffle: true,
        unclear: false, // Add 'Unclear' category
        description: true,
        max_categories: 30, // max categories can be created
        require_naming_explanation: REQUIRE_EXPLANATION.NO, // Ask reasons for naming the Categories
      },
      type: CARD_SORT_TYPES.OPEN, // Open, Closed, Hybrid
      require_explanation: REQUIRE_EXPLANATION.NO, // Ask for understanding of Card names
    }),
    ...otherAttrs,
  };
};

export const getUiTaskObj = (uiTaskType, position = 0, taskParams = {}, pcheck = YESNO.NO) => {
  if (!uiTaskType) return null;

  const { params } = getTaskType(uiTaskType, pcheck);
  const { type, surveyType = null, five_second = 0 } = params;

  const initialAttrs = {
    type,
    five_second,
    expected_time: five_second === YESNO.YES ? 5 : 0,
    show_alert: uiTaskType === FIVESEC_TASK || uiTaskType === FIRSTCLICK_TASK ? YESNO.YES : YESNO.NO,
    surveyType: surveyType && typeof surveyType === "object" ? surveyType[0] : surveyType, // Object means array here - only comes in case LikertType Survey Question
    success_type:
      type === HTML_PROTOTYPE
        ? SUCCESS_CRITERIA.RULES
        : type !== EXTERNAL_PROTOTYPE && five_second === YESNO.YES
          ? SUCCESS_CRITERIA.TIME_COMPLETION
          : SUCCESS_CRITERIA.NONE,
    ...(type === HTML_PROTOTYPE ? { proto_rules: getEmptyProtoRules(), proto_settings: { replay: true, mouseTrail: true } } : {}),
    ...taskParams,
  };
  return getTaskObj(position, initialAttrs, uiTaskType);
};

// Success rules for an HTML prototype task. The rules engine in the API (html-prototype/engine) reads this shape.
// No success or fail yet: the editor adds them as conditions are filled in (an empty list would be invalid).
export const getEmptyProtoRules = () => ({ version: 1, expectedPath: [], autoEndOn: "success" });

export const getUiTaskType = (
  taskType,
  surveyQType = null,
  fiveSecond = 0,
  test_type = null,
  pcheck = YESNO.NO,
  isSurveyTest = false
) => {
  // Empty Task
  if (taskType === EMPTY) return getTaskType(taskType, pcheck, isSurveyTest);

  const taskTypes = getTaskTypes(test_type, pcheck);

  return taskTypes.find(({ params: { type, five_second, surveyType } }) => {
    if (taskType === SURVEY) {
      return surveyType && typeof surveyType === "object" ? surveyType.includes(surveyQType) : surveyType === surveyQType;
    }

    return type === taskType && five_second === Number(fiveSecond);
  });
};

export const getSuccessCriterias = (test_type, testing_device = DEVICE_TYPE.COMPUTER, fetchAll = false, isFigmaPath = false) => {
  const { NONE, ANSWER, SUCCESS_URL, TIME_COMPLETION } = SUCCESS_CRITERIA;

  const criterias = [
    { value: NONE, label: "No Criteria", label2: "None" },
    { value: TIME_COMPLETION, label: "Task Completion Time", label2: "Time" },
  ];

  const websiteCriterias = [...criterias, { value: SUCCESS_URL, label: `Goal Screen URL`, label2: "URL" }];

  const prototypeCriterias = [
    { value: NONE, label: "No Criteria", label2: "None" },
    { value: SUCCESS_URL, label: "Goal-based", label2: "Path" },
  ];

  if (isFigmaPath) return prototypeCriterias;
  else if (Number(test_type) !== TEST_TYPE.WEBSITE && !fetchAll) return criterias;

  return Number(testing_device) === DEVICE_TYPE.COMPUTER
    ? [...websiteCriterias, { value: ANSWER, label: "Preset Answer", label2: "Answer" }]
    : websiteCriterias;
};

export const getLikertScaleTypes = () => {
  const { SATISFACTION, ENGAGEMENT, NUMBERS, CUSTOM } = LIKERT_TYPES;

  return [
    { value: SATISFACTION, label: "Satisfaction" },
    { value: ENGAGEMENT, label: "Engagement" },
    { value: NUMBERS, label: "Number" },
    { value: CUSTOM, label: "Custom Text" },
  ];
};

export const getSimpleInputTypes = () => {
  const { TEXT, NUMBER, EMAIL, DATE } = SIMPLE_INPUT_TYPES;

  return [
    { value: TEXT, label: "Text" },
    { value: NUMBER, label: "Numeric" },
    { value: EMAIL, label: "Email" },
    { value: DATE, label: "Date" },
  ];
};

export const getSimpleInputType = (value) => getSimpleInputTypes()?.find((type) => type?.value === value);

export const getLikertScaleOptions = (
  taskId,
  scaleType,
  number = LIKERT_5PT,
  options = null,
  persistPrevName = false,
  isMatrixQuestionTask = false
) => {
  const { SATISFACTION, ENGAGEMENT, NUMBERS, CUSTOM } = LIKERT_TYPES;

  const getOptions = (scaleType, number) => {
    const likert5points = {
      [CUSTOM]: Array(5).fill(""),
      [NUMBERS]: ["1", "2", "3", "4", "5"],
      [ENGAGEMENT]: ["Strongly disagree", "Disagree", "Neither agree nor disagree", "Agree", "Strongly agree"],
      [SATISFACTION]: [
        "Very satisfied",
        "Somewhat satisfied",
        "Neither satisfied nor dissatisfied",
        "Somewhat dissatisfied",
        "Very dissatisfied",
      ],
    };

    // Add "Somewhat disagree" at 3rd position and "Somewhat agree" at 5th position
    const engagementArr = [...likert5points[ENGAGEMENT]];
    engagementArr.splice(2, 0, "Somewhat disagree"); // Add "Somewhat disagree" at 3rd position
    engagementArr.splice(4, 0, "Somewhat agree"); // Add "Somewhat agree" at 5th position

    const satisfactionArr = ["Extremely satisfied", ...likert5points[SATISFACTION], "Extremely dissatisfied"];
    const likert7points = {
      [ENGAGEMENT]: engagementArr,
      [CUSTOM]: [...likert5points[CUSTOM], "", ""],
      [NUMBERS]: [...likert5points[NUMBERS], "6", "7"],
      [SATISFACTION]: satisfactionArr,
    };

    const satisfactionArrFor10pt = [
      "Extremely Satisfied",
      "Almost Completely Satisfied",
      "Very Satisfied",
      "Satisfied",
      "Somewhat Satisfied",
      "Neutral (Neither Satisfied nor Dissatisfied)",
      "Somewhat Dissatisfied",
      "Dissatisfied",
      "Very Dissatisfied",
      "Extremely Dissatisfied",
    ];

    const engagementArrFor10pt = [
      "Extremely Engaging",
      "Almost Fully Engaging",
      "Very Engaging",
      "Engaging",
      "Moderately Engaging",
      "Neutral (Neither Engaged nor Disengaged)",
      "Somewhat Engaging",
      "Slightly Engaging",
      "Barely Engaging",
      "Not at all Engaging",
    ];

    const likert10points = {
      [ENGAGEMENT]: engagementArrFor10pt,
      [CUSTOM]: [...likert7points[CUSTOM], "", "", ""],
      [NUMBERS]: [...likert7points[NUMBERS], "8", "9", "10"],
      [SATISFACTION]: satisfactionArrFor10pt,
    };

    if (isMatrixQuestionTask) {
      return number === MATRIX_INPUT_TYPES.FIVE_POINTS
        ? likert5points[scaleType]
        : number === MATRIX_INPUT_TYPES.SEVEN_POINTS
          ? likert7points[scaleType]
          : likert10points[scaleType];
    }

    return number === LIKERT_5PT
      ? likert5points[scaleType]
      : number === LIKERT_7PT
        ? likert7points[scaleType]
        : likert10points[scaleType];
  };

  const optionsArr = getOptions(scaleType, number);

  // New Options
  if (!options) return getSurveyOptionsObj(taskId, optionsArr);

  // in case of Custom Likert Scale, persist the previous name
  const usePrevName = persistPrevName && scaleType === CUSTOM;

  // Persist the previous object and just update the name
  const newOptions = optionsArr.map((name, position) => {
    if (options[position]) {
      const obj = { ...options[position] };
      return { ...obj, position, name: usePrevName ? obj.name : name };
    }

    return getSurveyOptionObj(taskId, name, position);
  });
  return getSurveyOptionsObj(taskId, newOptions);
};

export const getYesNoOptions = (taskId, options = [], addMaybe = false) => {
  let newOptions = options?.length ? [...options] : getSurveyOptionsObj(taskId, ["Yes", "No"]);

  if (addMaybe) {
    const maybeOption = getSurveyOptionObj(taskId, "Maybe", 2);
    if (newOptions.length >= 3) newOptions[2] = maybeOption;
    else newOptions.splice(2, 0, maybeOption);
  } else {
    if (newOptions.length >= 3) newOptions.splice(2, 1);
  }
  return getSurveyOptionsObj(taskId, newOptions);
};

export const manipulateTaskObj = (task, test = {}, deleteIsNew = false) => {
  const { pcheck = YESNO.NO } = test;

  // Repalce html unicdoe for backward compatibility
  const description = task?.description?.replaceAll("&nbsp;", " ").replaceAll("&amp;nbsp;", " ").replaceAll("&amp;nbsp", " ");

  const {
    id = getRandomID(),
    type,
    position,
    required,
    options = [],
    answer_type,
    five_second,
    task_shuffle,
    success_type,
    subTasks = [],
    expected_time,
    shuffle_images,
    useOtherOption,
    likertOptionType,
    addMaybe = false,
    surveyType = null,
    allowMultiSelect = false,
    addImages = task.images ? task.images.length > 0 : false,
    use_options_as_img = false,
  } = task;

  // Remove isNew attribute if it's not required
  if (deleteIsNew && task?.isNew !== undefined) delete task.isNew;

  // Migrate the options to Array of Objects
  if (options?.length === 0 || options?.every((option) => typeof option === "string")) {
    task.options = getSurveyOptionsObj(id, options);
  }
  return {
    ...task,
    id,
    addMaybe,
    addImages,
    surveyType,
    description,
    allowMultiSelect,
    use_options_as_img,
    position: Number(position),
    required: Number(required),
    answer_type: Number(answer_type),
    five_second: Number(five_second),
    task_shuffle: Number(task_shuffle),
    success_type: Number(success_type),
    expected_time: Number(expected_time),
    shuffle_images: Number(shuffle_images),
    useOtherOption: Number(useOtherOption),
    likertOptionType: Number(likertOptionType),
    subTasks: subTasks.map((task) => manipulateTaskObj(task, test)),
    type:
      Number(five_second) === YESNO.YES
        ? type === FIVESEC
          ? FIVESEC
          : pcheck === YESNO.YES
            ? EXTERNAL_PROTOTYPE
            : DEFAULT
        : Number(type),
  };
};

export const getNewUseShuffleType = (surveyType, likertOptionType, use_shuffle_options) => {
  const { CHECKBOX, LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, RANKING, MATRIX } = SURVEY_TYPES;
  const useShuffleOptions =
    surveyType === CHECKBOX ||
    surveyType === RANKING ||
    ([LIKERT_5PT, LIKERT_7PT, LIKERT_10PT].includes(surveyType) && likertOptionType === LIKERT_TYPES.CUSTOM) ||
    (surveyType === MATRIX && Number(likertOptionType) === LIKERT_TYPES.CUSTOM)
      ? use_shuffle_options
      : false;

  return useShuffleOptions;
};

export const getPrevAnswersConfig = (task, tasks, notCheckTasks = false) => {
  const orderedTasks = flattenTasksWithOrder(tasks);
  const activeTaskDisplayNum = orderedTasks.find((t) => t.id === task?.id)?.displayNumber;
  if (
    !task ||
    activeTaskDisplayNum <= 1 ||
    !(
      [SURVEY_TYPES.CHECKBOX, SURVEY_TYPES.RANKING].includes(task?.surveyType) ||
      ([SURVEY_TYPES.LIKERT_5PT, SURVEY_TYPES.LIKERT_7PT, SURVEY_TYPES.LIKERT_10PT, SURVEY_TYPES.MATRIX].includes(task?.surveyType) &&
        task?.likertOptionType === LIKERT_TYPES.CUSTOM)
    )
  ) {
    if (notCheckTasks) return false;

    return null; // not eligible at all
  }
  if (notCheckTasks) return true;

  let prevConfig = task?.prev_answers_config || { enabled: false, task_ids: [] };

  const validTaskIds = (prevConfig.task_ids || []).filter((tid) => {
    const refTask = findTaskById(orderedTasks, tid.id);
    return (
      refTask &&
      refTask?.displayNumber < activeTaskDisplayNum &&
      ([SURVEY_TYPES.CHECKBOX, SURVEY_TYPES.RANKING].includes(refTask?.surveyType) ||
        ([SURVEY_TYPES.LIKERT_5PT, SURVEY_TYPES.LIKERT_7PT, SURVEY_TYPES.LIKERT_10PT, SURVEY_TYPES.MATRIX].includes(
          refTask?.surveyType
        ) &&
          refTask?.likertOptionType === LIKERT_TYPES.CUSTOM))
    );
  });

  if (!validTaskIds.length) {
    return null; // no valid source tasks left
  }

  return {
    enabled: true,
    task_ids: validTaskIds,
  };
};

// helper to find a task by id in tasks or their subTasks
export const findTaskById = (tasks, id) => {
  const ts = tasks || [];
  let found = ts?.find((t) => t.id === id);
  if (found) return found;

  for (const t of ts) {
    if (Array.isArray(t.subTasks)) {
      const sub = t.subTasks.find((st) => st.id === id);
      if (sub) return sub;
    }
  }

  return null;
};

// Standard NPS buckets: 0-6 Detractors, 7-8 Passives, 9-10 Promoters.
export const getNpsResponseType = (name) => {
  const score = Number(name);
  if (score >= 9) return "Promoter";
  if (score >= 7) return "Passive";
  return "Detractor";
};

// NPS score = % Promoters - % Detractors. answeredCount (not totalResponses) is used as the
// percentage base so testers who skipped this optional question don't dilute the score.
export const getNpsScore = (optionsWithData = []) => {
  const promoterCount = optionsWithData
    .filter(({ name }) => getNpsResponseType(name) === "Promoter")
    .reduce((sum, { count }) => sum + count, 0);
  const passiveCount = optionsWithData
    .filter(({ name }) => getNpsResponseType(name) === "Passive")
    .reduce((sum, { count }) => sum + count, 0);
  const detractorCount = optionsWithData
    .filter(({ name }) => getNpsResponseType(name) === "Detractor")
    .reduce((sum, { count }) => sum + count, 0);
  const answeredCount = promoterCount + passiveCount + detractorCount;

  const promoterPercent = getPercent(promoterCount, answeredCount);
  const passivePercent = getPercent(passiveCount, answeredCount);
  const detractorPercent = getPercent(detractorCount, answeredCount);

  return {
    answeredCount,
    promoterCount,
    passiveCount,
    detractorCount,
    promoterPercent,
    passivePercent,
    detractorPercent,
    score: promoterPercent - detractorPercent,
  };
};

// helper function
export const isEligibleTaskToCarryFrom = (t, activeTask, activeTaskDisplayNum, forCriteria = false) => {
  // NPS's own fixed 0-10 options can be carried forward INTO other eligible questions, but NPS itself
  // is never a valid destination (see getPrevAnswersConfig) — its options must always stay fixed 0-10.
  let defaultTypesToCarryFrom = [SURVEY_TYPES.CHECKBOX, SURVEY_TYPES.RANKING, SURVEY_TYPES.NPS];
  if (forCriteria) {
    defaultTypesToCarryFrom.push(SURVEY_TYPES.MATRIX);
  }
  return (
    t.type === TASK_TYPES.SURVEY &&
    (defaultTypesToCarryFrom.includes(t?.surveyType) ||
      ([SURVEY_TYPES.LIKERT_5PT, SURVEY_TYPES.LIKERT_7PT, SURVEY_TYPES.LIKERT_10PT, SURVEY_TYPES.MATRIX].includes(t?.surveyType) &&
        t?.likertOptionType === LIKERT_TYPES.CUSTOM)) &&
    t?.displayNumber < (activeTaskDisplayNum ?? Infinity)
  );
};

// Flatten tasks into an ordered list with numbering, skipping parent if it has subTasks
export const flattenTasksWithOrder = (tasks, visited = new Set(), counter = { num: 0 }) => {
  let result = [];

  for (const task of tasks) {
    if (Number(task?.status) === TASK_STATUS.DELETED) continue;
    if (visited.has(task.id)) continue; // skip duplicates
    visited.add(task.id);

    if (Array.isArray(task.subTasks) && task.subTasks.length > 0) {
      // Skip parent itself, only process children
      result = result.concat(flattenTasksWithOrder(task.subTasks, visited, counter));
    } else {
      // Normal task, give it a number
      counter.num += 1;
      result.push({ ...task, displayNumber: counter.num });
    }
  }

  return result;
};

export const updateTaskWithCarryForwardOptions = (task, newTasks) => {
  const orderedTasks = flattenTasksWithOrder(newTasks);
  const taskDisplayNum = orderedTasks.find((t) => t.id === task?.id)?.displayNumber;
  if (task?.prev_answers_config?.enabled) {
    if (task?.prev_answers_config?.task_ids?.length === 0) {
      return { ...task, prev_answers_config: null };
    } else {
      task.prev_answers_config.task_ids = task.prev_answers_config.task_ids.filter((id) => {
        const optionTask = orderedTasks.find((t) => t.id === id["id"]);
        if (optionTask && isEligibleTaskToCarryFrom(optionTask, task, taskDisplayNum, id.use_criteria)) {
          return true;
        }
        return false;
      });

      //update carry_forward_options
      let new_carry_forward_options = [];

      task.prev_answers_config?.task_ids?.forEach((id) => {
        if (id?.use_criteria !== true) {
          const optionTask = orderedTasks.find((t) => t.id === id["id"]);
          if (optionTask?.options) {
            optionTask.options.forEach((op) => {
              new_carry_forward_options.push({
                ...op,
                id: `${optionTask.id}_${op.id}`, // merged unique id
                task_id: optionTask.id,
                linked_option_id: op.id,
              });
            });
          }
          if (optionTask?.carry_forward_options) {
            optionTask.carry_forward_options.forEach((op) => {
              new_carry_forward_options.push({
                ...op,
                id: `${optionTask.id}_${op.id}`, // merged unique id
                task_id: optionTask.id,
                linked_option_id: op.id,
              });
            });
          }
        }
      });

      task.carry_forward_options = new_carry_forward_options;

      //update carry_forward_criterias
      let new_carry_forward_criterias = [];

      task.prev_answers_config?.task_ids?.forEach((id) => {
        if (id?.use_criteria === true) {
          const criTask = orderedTasks.find((t) => t.id === id["id"]);
          if (criTask?.criterias) {
            criTask.criterias.forEach((cri) => {
              new_carry_forward_criterias.push({
                ...cri,
                id: `${criTask.id}_${cri.id}`, // merged unique id
                task_id: criTask.id,
                linked_criteria_id: cri.id,
              });
            });
          }
          if (criTask?.carry_forward_criterias) {
            criTask.carry_forward_criterias.forEach((cri) => {
              new_carry_forward_criterias.push({
                ...cri,
                id: `${criTask.id}_${cri.id}`, // merged unique id
                task_id: criTask.id,
                linked_criteria_id: cri.id,
              });
            });
          }
        }
      });

      task.carry_forward_criterias = new_carry_forward_criterias;
    }
  }
  if (Array.isArray(task.subTasks) && task.subTasks.length > 0) {
    task.subTasks = task.subTasks.map((sub) => updateTaskWithCarryForwardOptions(sub, orderedTasks));
  }
  return task;
};

export const getCarryOptionsRowsCount = (task) => {
  const rowCarry = task?.prev_answers_config?.task_ids?.filter((t) => t.use_criteria === true)?.length || 0;
  const colCarry = (task?.prev_answers_config?.task_ids?.length || 0) - rowCarry;

  return { rowCount: rowCarry, optionCount: colCarry };
};

export const isEligibleToAddAnsPiping = (task, tasks) => {
  const orderedTasks = flattenTasksWithOrder(tasks);
  const taskDisplayNum = orderedTasks.find((t) => t.id === task?.id)?.displayNumber;
  if (!task || taskDisplayNum <= 1) {
    return false;
  }
  return true;
};
export const isEligibleTaskForAnswerPiping = (t, activeTask, activeTaskDisplayNum) => {
  let defaultTypesToCarryFrom = [
    SURVEY_TYPES.CHECKBOX,
    SURVEY_TYPES.RANKING,
    SURVEY_TYPES.LIKERT_5PT,
    SURVEY_TYPES.LIKERT_7PT,
    SURVEY_TYPES.LIKERT_10PT,
    SURVEY_TYPES.MATRIX,
    SURVEY_TYPES.RADIO,
    SURVEY_TYPES.SIMPLE_INPUT,
    SURVEY_TYPES.TEXT,
    SURVEY_TYPES.NPS,
  ];
  return (
    t.type === TASK_TYPES.SURVEY &&
    defaultTypesToCarryFrom.includes(t?.surveyType) &&
    t?.displayNumber < (activeTaskDisplayNum ?? Infinity)
  );
};

export const hasTagsInDescription = (description) => {
  if (!description) return false;
  const container = document.createElement("div");
  container.innerHTML = description;
  return container.querySelector("span.ql-tag") !== null;
};

export const replaceTagsWithAnswers = (description, tasks, taskResponses) => {
  if (!description) return "";

  // Create a DOM parser
  const parser = new DOMParser();
  const doc = parser.parseFromString(description, "text/html");

  // Find all tag spans
  const tagNodes = doc.querySelectorAll("span.ql-tag");

  tagNodes.forEach((node) => {
    const taskId = node.getAttribute("data-task-id");
    const task = tasks.find((t) => String(t.id) === String(taskId));

    if (task && taskResponses) {
      const ts = taskResponses?.find((t) => t.task_id === task.id);

      let names = [];
      if (ts?.surveyAnswers?.[0]?.answers) {
        if (task.surveyType === SURVEY_TYPES.MATRIX || task?.surveyQuestions?.[0].type === SURVEY_TYPES.MATRIX) {
          ts.surveyAnswers.forEach((sans) => {
            const taskOrigOptions = task?.options ?? task?.surveyQuestions?.[0]?.options ?? [];
            const taskPrevCarryOptions = task?.carry_forward_options ?? task?.carry_forward_options ?? [];
            const taskOptions = [...taskOrigOptions, ...taskPrevCarryOptions];
            const opt = taskOptions?.find((o) => String(o.id) === String(sans.answers));
            const taskOrigCriterias = task?.criterias ?? task?.surveyQuestions?.[0]?.criterias ?? [];
            const taskPrevCarryCriterias = task?.carry_forward_criterias ?? task?.carry_forward_criterias ?? [];
            const taskCriterias = [...taskOrigCriterias, ...taskPrevCarryCriterias];
            const cri = taskCriterias?.find((o) => String(o.id) === String(sans.criteria_id));
            names.push(`${cri?.name + ": " + opt?.name}`);
          });
        } else {
          let ans = ts.surveyAnswers[0].answers;
          const answersArray = Array.isArray(ans) ? ans : [ans];
          names = answersArray
            .map((a) => {
              const taskOrigOptions = task?.options ?? task?.surveyQuestions?.[0]?.options ?? [];
              const taskPrevCarryOptions = task?.carry_forward_options ?? task?.carry_forward_options ?? [];
              const taskOptions = [...taskOrigOptions, ...taskPrevCarryOptions];
              const opt = taskOptions?.find((o) => String(o.id) === String(a));
              return opt ? opt.name : a;
            })
            .filter(Boolean);
        }
      }
      if (ts?.surveyAnswers?.[0]?.others) {
        names.push(ts?.surveyAnswers?.[0]?.others);
      }
      // Create replacement span with space & styling
      const replacement = doc.createElement("span");
      replacement.className = "answer-highlight"; // custom CSS
      replacement.innerText = ` ${names.join(", ")} `; // add spaces before/after

      node.parentNode.replaceChild(replacement, node);
    } else {
      // If no answer, remove the tag
      node.parentNode.removeChild(node);
    }
  });

  return doc.body.innerHTML;
};

export function removeTagCloseNodes(description) {
  if (!description) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(description, "text/html");

  // find all ql-tag-close spans and remove them
  const closeNodes = doc.querySelectorAll("span.ql-tag-close");
  closeNodes.forEach((node) => node.remove());

  return doc.body.innerHTML;
}

export const updateTaskWithAnsPipeQues = (task, newTasks, sendDecription = true) => {
  const orderedTasks = flattenTasksWithOrder(newTasks);
  const taskDisplayNum = orderedTasks.find((t) => t.id === task?.id)?.displayNumber;
  if (task?.description && hasTagsInDescription(task.description)) {
    const container = document.createElement("div");
    container.innerHTML = task.description;

    const tagNodes = container.querySelectorAll("span.ql-tag");
    tagNodes.forEach((node) => {
      const tagTaskId = node.getAttribute("data-task-id");
      const fromTask = orderedTasks.find((t) => String(t.id) === String(tagTaskId));

      if (!fromTask) {
        // Remove tag if task not found
        node.remove();
      } else if (isEligibleTaskForAnswerPiping(fromTask, task, taskDisplayNum)) {
        // Update displayNumber in tag
        const displayNumber = fromTask.displayNumber ?? "";
        node.setAttribute("data-num", displayNumber);
        const labelNode = node.querySelector(".ql-tag-label");
        if (labelNode) {
          labelNode.innerText = `Q${displayNumber} Answer`;
        }
        const optionsStr = getOptionsName(fromTask?.options, true);
        if (optionsStr !== "") {
          node.setAttribute("data-option-names", optionsStr);
        } else {
          node.removeAttribute("data-option-names");
        }
      } else {
        // Remove tag if not eligible
        node.remove();
      }
    });

    task.description = container.innerHTML;
  }

  if (Array.isArray(task.subTasks) && task.subTasks.length > 0) {
    task.subTasks = task.subTasks.map((sub) => updateTaskWithAnsPipeQues(sub, orderedTasks, false));
  }
  if (sendDecription) {
    return task?.description;
  }
  return task;
};

export const countTagsInDescription = (description) => {
  if (!description) return 0;

  // Create a temporary DOM element to parse description
  const container = document.createElement("div");
  container.innerHTML = description;

  // Select all ql-tag spans
  const tags = container.querySelectorAll("span.ql-tag");
  return tags.length;
};

export const getOptionsName = (options, sendOptionsStr = false) => {
  const validNames = options.filter((opt) => String(opt?.name ?? "").trim() !== "").map((opt) => String(opt.name).trim());
  if (sendOptionsStr) {
    if (validNames.length > 0) {
      return validNames.join(", ");
    } else return "";
  }
  return validNames;
};
export const removeExclusiveFromOptions = (options = []) => {
  return options.map((opt) => ({
    ...opt,
    is_exclusive: false,
  }));
};

export const isTaskExistsInDraftData = (dataLaunched, taskId) => {
  const foundTask = findTaskById(dataLaunched?.tasks, taskId);
  if (foundTask === null) return false;
  else return true;
};

export const isTaskUsedInAnotherTaskCarryOptions = (taskId, tasks) => {
  let isUsed = false;
  const orderedTasks = flattenTasksWithOrder(tasks);
  orderedTasks.forEach((task) => {
    const config = task?.prev_answers_config;

    if (!config || !config.enabled || !Array.isArray(config.task_ids)) {
      return;
    }

    config.task_ids.forEach((item) => {
      if (String(item.id) === String(taskId)) {
        isUsed = true;
      }
    });
  });

  return isUsed;
};

export const isTaskUsedInAnotherTaskDescr = (taskId, tasks) => {
  if (!tasks?.length) return false;

  const parser = new DOMParser();

  let isUsed = false;
  const orderedTasks = flattenTasksWithOrder(tasks);
  orderedTasks.forEach((task) => {
    if (!task?.description) return;

    const doc = parser.parseFromString(task.description, "text/html");
    const tagNodes = doc.querySelectorAll("span.ql-tag");

    tagNodes.forEach((node) => {
      const refTaskId = node.getAttribute("data-task-id");

      if (String(refTaskId) === String(taskId)) {
        isUsed = true;
      }
    });
  });

  return isUsed;
};

export function isDefaultOrPrototypeTask(t) {
  return Number(t?.type) === TASK_TYPES.DEFAULT || Number(t?.type) === TASK_TYPES.EXTERNAL_PROTOTYPE;
}

export function isHtmlPrototypeTask(t) {
  return Number(t?.type) === TASK_TYPES.HTML_PROTOTYPE;
}

export function getNewActiveTaskIds(tasks = [], activeTaskIds = []) {
  if (!tasks?.length) return [];

  const [activeParentId, activeChildId] = activeTaskIds || [];
  const validTasks = tasks?.filter((t) => t?.status !== TASK_STATUS.DELETED);

  // -------- CASE 1: Active child deleted --------
  const parentTask = validTasks.find((t) => t.id === activeParentId);

  if (activeChildId && parentTask?.subTasks?.length) {
    const subTasks = parentTask?.subTasks?.filter((t) => t?.status !== TASK_STATUS.DELETED);

    // If child still exists → no change
    const childExists = subTasks?.some((t) => t.id === activeChildId);
    if (childExists) return activeTaskIds;

    const newChild = subTasks?.length > 0 ? subTasks[0] : null;
    return [activeParentId, newChild ? newChild?.id : null];
  }

  // -------- CASE 2: Active parent deleted --------
  const parentExists = validTasks.some((t) => t.id === activeParentId);
  if (parentExists) return activeTaskIds;

  // Find closest parent (prefer previous, fallback next)
  let newParentIndex = validTasks?.findIndex((t) => t?.id === activeParentId);

  // Since it's deleted, index will be -1 → fallback strategy
  newParentIndex = Math.max(0, validTasks?.length - 1);

  const newParent = validTasks[newParentIndex];

  if (!newParent) return [];

  return [newParent.id, newParent?.subTasks?.length ? newParent.subTasks[0].id : null];
}

// Get Valid Tree Task Answers
export function getValidTreeTaskAnswers(task) {
  if (!task || Number(task?.type) !== TREE) return task?.tree_answer;

  const { tree_answer = "", tree = {} } = task || {};
  const treeAnswersArr = getValidJSON(tree_answer);
  const allAnswers = treeAnswersArr?.map((pathId) => ({ pathId, path: getNodePath(tree?.treeData || [], pathId, true) }));
  return JSON.stringify(allAnswers?.filter((obj) => Boolean(obj?.path))?.map((obj) => obj?.pathId));
}

// Get Total Options Count
export function getTotalOptionsCount(task = null, tasks = []) {
  const totalOptions = task?.options?.length || 0;
  if (task?.prev_answers_config) {
    const { task_ids = [] } = task?.prev_answers_config;

    const flattenTasksList = flattenTasks(tasks);
    const lengths = task_ids?.map(({ id, options = [] }) => {
      const task = flattenTasksList?.find((t) => t?.id === id);

      if (!task) return 0;
      return options?.length > 0 ? options?.length : task?.options?.length;
    });

    return totalOptions + lengths?.reduce((a, b) => a + b, 0);
  }

  return totalOptions;
}
